import axios from "axios";
import { toast } from "react-toastify";

// RIS (TeleRad / Softmed PACS) hand-off for radiology studies — when the
// technician marks a study "Sent to lab" on the radiology board
// (radiology-technician/IpdRadiologyOrders).
//
// The frontend never talks to technocratzindia directly: the Django backend's
// `telerad/patient_registration/` view fetches the PACS token, resolves TESTCODE
// from the medical_service UUID, stores a PatientDiacomTest row and forwards the
// DicomModInsertExternal call. This module only decides *when* to call it:
//   - facility has ris_integrated (from get_logged_in_user_info)
//   - the service belongs to the "Radiology" service group
//
// Payload follows the PACS format: dates YYYYMMDD, time HHMMSS, TESTCODE is the
// integer MedicalService id returned by get_medical_services/ (the UUID goes
// separately as medical_service_id for our own DICOM record).

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL || "";
const RADIOLOGY_GROUP = "RADIOLOGY";

const pad2 = (n) => String(n).padStart(2, "0");

const localDate = (d = new Date()) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const localTime = (d = new Date()) =>
  `${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}`;

// "2026-09-21" → "20260921" (PACS date format); "" stays "".
const compactDate = (iso) => String(iso || "").slice(0, 10).replace(/-/g, "");

// "Male" / "Female" / "Other" (get_gender_display) or "M"/"F"/"O" → DICOM sex code.
const toPacsSex = (gender) => {
  const g = String(gender || "").trim().toUpperCase();
  if (!g) return "";
  return g[0] === "M" || g[0] === "F" ? g[0] : "O";
};

const asList = (res) =>
  Array.isArray(res?.data?.data)
    ? res.data.data
    : Array.isArray(res?.data?.service_list)
      ? res.data.service_list
      : [];

const isPkId = (id) => /^\d+$/.test(String(id));

// IPD screens carry MedicalService's integer PK ("3709"), which
// get_medical_services/ cannot resolve — it filters on the UUID only. The
// service-master detail endpoint takes the PK and returns the same service
// (UUID, name, group) under slightly different keys.
async function fetchServicesByPk(pks, headers) {
  const services = await Promise.all(
    pks.map((pk) =>
      axios
        .get(`${REACT_APP_BACKEND_ROUTE_URL}ipd/billing/service-master/services/${pk}/`, {
          headers,
        })
        .then((res) => res?.data?.service),
    ),
  );
  return services
    .filter(Boolean)
    .map((s) => ({ ...s, service_group_name: s.group_name }));
}

/** MedicalService rows for UUIDs and/or integer PKs, every service group. */
export async function fetchMedicalServices(serviceIds, headers) {
  const uuids = serviceIds.filter((id) => !isPkId(id));
  const [byUuid, byPk] = await Promise.all([
    uuids.length
      ? axios
          .post(
            `${REACT_APP_BACKEND_ROUTE_URL}get_medical_services/`,
            { medical_service_ids: uuids },
            { headers },
          )
          .then(asList)
      : [],
    fetchServicesByPk(serviceIds.filter(isPkId), headers),
  ]);
  return [...byUuid, ...byPk];
}

// "RADIOLOGY" / "LABORATORY". The service master sends group_code;
// get_medical_services/ only the group's name, which reads the same uppercased.
export const serviceGroupOf = (svc) =>
  String(svc?.group_code || svc?.service_group_name || "").trim().toUpperCase();

// A real PatientLabTest order number (LAB-2026-0188 / RAD-2026-109). Boards fall
// back to a lab_test_id or a made-up key when an order has none; those are not
// sent, and the RIS/LIS row then gets its own RIS_/LIS_ number.
export const isHospitalOrderNo = (no) => /^(LAB|RAD)-/i.test(String(no || "").trim());

async function fetchPatient(patientId, headers) {
  const res = await axios.post(
    `${REACT_APP_BACKEND_ROUTE_URL}get_filtered_patient_data/`,
    {
      filter_type: "patient_id",
      filter_details: { patient_id: patientId },
      multipleResults: true,
    },
    { headers },
  );
  const list = Array.isArray(res?.data?.patient_details) ? res.data.patient_details : [];
  return list[0] || null;
}

/** Every PatientDiacomTest row of a patient (UHID), newest first. */
export async function fetchPatientRisRecords(patientId, headers) {
  const res = await axios.get(
    `${REACT_APP_BACKEND_ROUTE_URL}telerad/patient_dicom_records/${encodeURIComponent(patientId)}/`,
    { headers },
  );
  return Array.isArray(res?.data?.data) ? res.data.data : [];
}

// DICOM modality code guessed from the service name; PACS accepts "" too.
const guessModality = (name) => {
  const n = String(name || "").toUpperCase();
  if (/\bMRI\b|\bMR\b/.test(n)) return "MR";
  if (/\bCT\b/.test(n)) return "CT";
  if (/X-?RAY|RADIOGRAPH/.test(n)) return "CR";
  if (/ULTRASO|\bUSG\b|SONOGRAPH|DOPPLER|\bECHO/.test(n)) return "US";
  if (/MAMMO/.test(n)) return "MG";
  if (/\bPET\b/.test(n)) return "PT";
  return "";
};

const postRegistration = (payload, headers) =>
  axios.post(`${REACT_APP_BACKEND_ROUTE_URL}telerad/patient_registration/`, payload, {
    headers,
  });

// PACS rejects studies for a TESTCODE it does not know. Register the test
// (DicomModTestInsertExternal via the backend) using the integer test_code the
// failed registration reported, so the caller can retry once.
async function registerTestWithPacs({ svc, testCode, userInfo, headers }) {
  const res = await axios.post(
    `${REACT_APP_BACKEND_ROUTE_URL}telerad/test_insert/`,
    {
      TESTCODE: String(testCode),
      TEST: svc.name || "",
      DEPT: svc.service_group_name || "Radiology",
      MODALITY: guessModality(svc.name),
      name: svc.name || "",
      description: svc.description || "",
      fee: svc.fee != null ? Number(svc.fee) : null,
      unit: svc.unit || "",
      active: true,
      expired: false,
      expiry_date: svc.expiry_date || null,
      deactivation_reason: null,
      snomed_code: svc.snomed_code || null,
      in_facility: true,
      hip_id: svc.hip_id || userInfo?.facility_id || "",
    },
    { headers },
  );
  return res?.data?.status === "Success";
}

// Takes already-resolved services (any group) and sends the Radiology ones.
// Never throws — failures surface as toasts only. Resolves to true only when
// every Radiology study was accepted.
async function sendRadiologyStudiesToRis({
  patientId,
  services,
  careContextId,
  referredBy,
  orderNo,
  userInfo,
  headers,
}) {
  try {
    if (!patientId) return false;
    const radiologyServices = services.filter((s) => serviceGroupOf(s) === RADIOLOGY_GROUP);
    if (!radiologyServices.length) return false;

    const now = new Date();
    const startDate = localDate(now);
    const startTime = localTime(now);

    const patient = await fetchPatient(patientId, headers);

    if (!patient) {
      toast.warn("RIS: patient details not found, radiology order not sent.");
      return false;
    }

    const results = await Promise.all(
      radiologyServices.map(async (svc) => {
        const serviceId = String(svc.medical_service_id);
        const payload = {
          AET: process.env.REACT_APP_TELERAD_AET || "DemoAET",
          STARTDATE: compactDate(startDate),
          STARTTIME: startTime,
          TESTCODE: svc.id != null ? String(svc.id) : serviceId,
          medical_service_id: serviceId,
          PERFPHYSNAME: "",
          DESCR: svc.name || "",
          REQPHYSICIAN: referredBy,
          REFPHYSNAME: referredBy,
          PATIENTID: patientId,
          PATIENTNAME: patient.name || "",
          PATIENTBIRTHDATE: compactDate(patient.dob),
          PATIENTSEX: toPacsSex(patient.gender),
          PATIENTAGE: patient.age != null ? String(patient.age) : "",
          PATIENTREGDATE: compactDate(startDate),
          ISPAID: "1",
          PATIENTMOBILE: patient.phone_no || "",
          PATIENTEMAIL: patient.email || "",
          // main_patientdiacomtest has NOT NULL on both columns; the backend
          // inserts whatever it receives, so send "" rather than omitting them.
          appointment_id: "",
          carecontext_id: careContextId ? String(careContextId) : "",
          // Stored as the PatientDiacomTest order_no, so the study and its order share one id.
          order_no: isHospitalOrderNo(orderNo) ? String(orderNo).trim() : "",
        };

        try {
          let res = await postRegistration(payload, headers);
          if (res?.data?.status === "Success") {
            return { ok: true, name: payload.DESCR };
          }

          // Failed: add the test to PACS and retry once.
          console.warn("[RIS] patient_registration failed, response:", res?.data);
          const testCode = svc.id ?? res?.data?.test_code;
          if (testCode != null && testCode !== "") {
            const testAdded = await registerTestWithPacs({ svc, testCode, userInfo, headers });
            if (testAdded) {
              res = await postRegistration(payload, headers);
              if (res?.data?.status === "Success") return { ok: true, name: payload.DESCR };
            }
          }
          return { ok: false, name: payload.DESCR, message: res?.data?.message };
        } catch (err) {
          return {
            ok: false,
            name: payload.DESCR,
            message: err?.response?.data?.message || err?.message,
          };
        }
      }),
    );

    const sent = results.filter((r) => r.ok);
    const failed = results.filter((r) => !r.ok);
    if (sent.length) {
      toast.success(`Radiology order sent to RIS: ${sent.map((r) => r.name).join(", ")}`);
    }
    failed.forEach((r) => {
      console.error("RIS registration failed:", r.name, r.message);
      toast.warn(`RIS registration failed for ${r.name}${r.message ? `: ${r.message}` : ""}`);
    });
    return failed.length === 0;
  } catch (err) {
    console.error("RIS integration error:", err);
    toast.warn("Could not send radiology order to RIS.");
    return false;
  }
}

/**
 * Registers one radiology study with the RIS when the technician marks it
 * "Sent to lab" on the radiology board — IPD, OPD and Emergency alike. ISPAID
 * is "1": OPD studies only reach that board once paid, IPD is billed on credit.
 *
 * @param {object} args
 * @param {string} args.patientId      UHID (PatientRecord.patient_id, e.g. "PAT-1314")
 * @param {Array}  args.serviceIds     the study's MedicalService ids (UUID or integer PK)
 * @param {string} args.careContextId  visit / admission care context
 * @param {string} args.doctorName     ordering doctor (REQPHYSICIAN)
 * @param {string} args.orderNo        the study's RAD- order number
 * @param {object} args.userInfo       usePermissions().userInfo
 * @param {object} args.headers        request headers incl. Authorization
 * @returns {Promise<boolean>} true when the RIS accepted the study
 */
export async function sendRadiologyOrderToRis({
  patientId,
  serviceIds,
  careContextId,
  doctorName,
  orderNo,
  userInfo,
  headers,
}) {
  let services;
  try {
    services = await fetchMedicalServices(serviceIds, headers);
  } catch (err) {
    console.error("RIS service lookup failed:", err);
    toast.warn("Could not send radiology order to RIS.");
    return false;
  }
  if (!services.some((s) => serviceGroupOf(s) === RADIOLOGY_GROUP)) {
    toast.warn("This study is not in the Radiology service group, so it was not sent to RIS.");
    return false;
  }
  return sendRadiologyStudiesToRis({
    patientId,
    services,
    careContextId,
    referredBy: doctorName,
    orderNo,
    userInfo,
    headers,
  });
}
